/**
 * /bookingflight/confirmation — Booking Confirmation (Phase 10).
 *
 * Reads the confirmed booking from BookingContext and shows the booking reference, mock
 * PNR, full flight details, and passenger list. Offers a downloadable mock-PDF ticket, a
 * print action (print-only stylesheet isolates the ticket), and a "Back to Home" that
 * resets the flow.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useBooking } from "../../context/BookingContext";
import { formatMoney } from "../../utils/fare";
import { downloadTicket } from "../../utils/ticket";
import "./confirmation.css";

const TYPE_LABEL = { adult: "Adult", child: "Child", infant: "Infant" };

export default function Confirmation() {
  const navigate = useNavigate();
  const { confirmation, resetBooking } = useBooking();

  if (!confirmation) return null; // guard redirects

  const { flight, passengers = [], contact = {}, fare = {}, reference, pnr } = confirmation;

  const goHome = () => {
    resetBooking();
    navigate("/");
  };

  const bookAnother = () => {
    resetBooking();
    navigate("/flights");
  };

  return (
    <section className="cf-page">
      {/* Success banner */}
      <div className="cf-banner">
        <div className="cf-banner__check">
          <i className="fas fa-check" aria-hidden="true" />
        </div>
        <h1 className="cf-banner__title">Booking Confirmed!</h1>
        <p className="cf-banner__text">
          Your booking is confirmed. A confirmation has been sent to{" "}
          <strong>{contact.email}</strong>.
        </p>
        <div className="cf-banner__refs">
          <div className="cf-ref">
            <span className="cf-ref__label">Booking Reference</span>
            <span className="cf-ref__value">{reference}</span>
          </div>
          <div className="cf-ref">
            <span className="cf-ref__label">PNR</span>
            <span className="cf-ref__value">{pnr}</span>
          </div>
        </div>
      </div>

      {/* Ticket */}
      <div className="cf-ticket" id="print-ticket">
        <div className="cf-ticket__head">
          <div className="cf-ticket__brand">
            <i className="fas fa-plane" aria-hidden="true" />
            <span>Falcon Destinations</span>
          </div>
          <span className="cf-ticket__status">
            <i className="fas fa-circle" aria-hidden="true" /> {confirmation.status || "CONFIRMED"}
          </span>
        </div>

        {/* Flight */}
        <div className="cf-section">
          <div className="cf-airline">
            <img src={flight.airlineLogo} alt={flight.airline} width={44} height={44} />
            <div>
              <div className="cf-airline__name">{flight.airline}</div>
              <div className="cf-airline__meta">{flight.flightNumber} · {flight.aircraft}</div>
            </div>
            <span className="cf-cabin">{flight.cabinClass}</span>
          </div>

          <div className="cf-route">
            <div className="cf-route__point">
              <span className="cf-route__time">{flight.departureTime}</span>
              <span className="cf-route__code">{flight.departureAirport.code}</span>
              <span className="cf-route__city">{flight.departureAirport.city}</span>
            </div>
            <div className="cf-route__mid">
              <span className="cf-route__dur">{flight.duration}</span>
              <div className="cf-route__line"><i className="fas fa-plane" aria-hidden="true" /></div>
              <span className="cf-route__stops">
                {flight.stopCount === 0
                  ? "Nonstop"
                  : `${flight.stopCount} stop${flight.stopCount > 1 ? "s" : ""} · ${flight.stopCities.join(", ")}`}
              </span>
            </div>
            <div className="cf-route__point cf-route__point--right">
              <span className="cf-route__time">
                {flight.arrivalTime}
                {flight.arrivalDayOffset > 0 && <sup> +{flight.arrivalDayOffset}</sup>}
              </span>
              <span className="cf-route__code">{flight.arrivalAirport.code}</span>
              <span className="cf-route__city">{flight.arrivalAirport.city}</span>
            </div>
          </div>

          <div className="cf-facts">
            <span><i className="fas fa-suitcase-rolling" aria-hidden="true" /> {flight.baggageAllowance}</span>
            <span><i className={`fas ${flight.refundable ? "fa-undo" : "fa-ban"}`} aria-hidden="true" /> {flight.refundable ? "Refundable" : "Non-refundable"}</span>
            <span><i className="fas fa-chair" aria-hidden="true" /> Class {flight.bookingClass}</span>
          </div>
        </div>

        {/* Passengers */}
        <div className="cf-section">
          <h3 className="cf-section__title">Passengers</h3>
          <div className="cf-pax-table">
            {passengers.map((p, i) => (
              <div className="cf-pax-row" key={i}>
                <span className="cf-pax-row__idx">{i + 1}</span>
                <span className="cf-pax-row__name">{p.fullName}</span>
                <span className="cf-pax-row__type">{TYPE_LABEL[p.type]}</span>
                <span className="cf-pax-row__pass">{p.passportNumber}</span>
                <span className="cf-pax-row__nat">{p.nationality}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Fare */}
        <div className="cf-section cf-fare">
          <div className="cf-fare__rows">
            <div><span>Base Fare</span><span>{formatMoney(fare.baseFare || 0, fare.currency)}</span></div>
            <div><span>Taxes</span><span>{formatMoney(fare.taxes || 0, fare.currency)}</span></div>
            <div><span>Service Fee</span><span>{formatMoney(fare.serviceFee || 0, fare.currency)}</span></div>
          </div>
          <div className="cf-fare__total">
            <span>Total Paid ({confirmation.paymentMethod || "Mock"})</span>
            <span className="cf-fare__total-value">{formatMoney(fare.total || 0, fare.currency)}</span>
          </div>
        </div>

        <p className="cf-ticket__note">
          This is a mock e-ticket generated for demonstration purposes only.
        </p>
      </div>

      {/* Actions */}
      <div className="cf-actions">
        <button type="button" className="cf-btn cf-btn--primary" onClick={() => downloadTicket(confirmation)}>
          <i className="fas fa-download" aria-hidden="true" /> Download Ticket
        </button>
        <button type="button" className="cf-btn" onClick={() => window.print()}>
          <i className="fas fa-print" aria-hidden="true" /> Print Ticket
        </button>
        <button type="button" className="cf-btn" onClick={bookAnother}>
          <i className="fas fa-plane" aria-hidden="true" /> Book Another
        </button>
        <button type="button" className="cf-btn cf-btn--ghost" onClick={goHome}>
          <i className="fas fa-home" aria-hidden="true" /> Back to Home
        </button>
      </div>
    </section>
  );
}
